import { store } from '../app/store';
import { API_BASE_URL } from '../app/api/apiSlice';
import { setCredentials } from '../features/auth/authSlice';

const post = (body, signal) => fetch(`${API_BASE_URL}reading`, {
  method: 'POST',
  credentials: 'include',
  headers: {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${store.getState().auth.token}`
  },
  body: JSON.stringify(body),
  signal
});

/** Same rule as baseQueryWithReauth: an expired access token gets one refresh. */
const refreshToken = async (signal) => {
  const response = await fetch(`${API_BASE_URL}auth/refresh`, { credentials: 'include', signal });
  if (!response.ok) return false;
  store.dispatch(setCredentials(await response.json()));
  return true;
};

/**
 * Streams a tarot reading from the backend, which holds the Gemini key and
 * voices each sentence (Azure Neural or Kokoro, chosen by the backend).
 *
 * `onSentence` receives `{ id, raw, audio?, format?, words? }` in order; the
 * `raw` of every sentence concatenated is the whole reading. Resolves when
 * the reading is complete; rejects on failure or abort.
 *
 * @param {object}   request
 * @param {Array}    request.cards       cards with `name` and `position`, already localized
 * @param {string}   request.spreadName
 * @param {string}   request.language    'en' | 'es'
 * @param {boolean}  request.voice       ask for narration audio
 * @param {object}   options
 * @param {Function} options.onSentence
 * @param {AbortSignal} options.signal
 */
export async function streamTarotReading(request, { onSentence, signal }) {
  const body = {
    cards: request.cards.map(({ name, position }) => ({ name, position })),
    spreadName: request.spreadName,
    language: request.language,
    voice: request.voice
  };

  let response = await post(body, signal);
  if (response.status === 403 && await refreshToken(signal)) {
    response = await post(body, signal);
  }

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new Error(data?.message || `Reading request failed (${response.status})`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true }).replace(/\r/g, '');

    let boundary;
    while ((boundary = buffer.indexOf('\n\n')) !== -1) {
      const { event, data } = parseEvent(buffer.slice(0, boundary));
      buffer = buffer.slice(boundary + 2);

      if (event === 'sentence') onSentence(data);
      else if (event === 'done') return;
      else if (event === 'error') throw new Error(data.message);
    }
  }

  throw new Error('The reading stream ended unexpectedly');
}

const parseEvent = (block) => {
  let event = 'message';
  let data = '';
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data += line.slice(5).trim();
  }
  return { event, data: data ? JSON.parse(data) : {} };
};
